<script setup>
import { ref } from 'vue'
import { useWebSiteStore } from '@/stores/website.store'
import { useNotify } from '@/composables/notify'
import { useRouter } from 'vue-router'
import i18n from '@/plugins/i18n'

const username = ref('')
const password = ref('')
const isPwd = ref(true)
const firstName = ref('')
const lastName = ref('')
const phoneNumber = ref('')
const email = ref('')

const loading = ref(false)

const websiteStore = useWebSiteStore()
const t = i18n.global.t
const locale = i18n.global.locale
const {notifyError, notifyInfo} = useNotify()
const router = useRouter()

const required = (val) => !!val || t('form.field.required')

/**
 * Let people paste a phone number.
 *
 * The masked tel input rejects anything that is not a bare digit, so pasting a number copied
 * from WhatsApp/contacts ("+39 340 123 4567", "340-123-4567", "0039340...") silently did
 * nothing. Here we take the pasted text, strip everything that is not a digit or a leading
 * plus, convert a "00" international prefix into "+", and feed the clean value to the model.
 */
const onPhonePaste = (event) => {
  const pasted = (event.clipboardData || window.clipboardData)?.getData('text') || ''
  if (!pasted.trim()) {
    return
  }

  event.preventDefault()
  event.stopPropagation()

  // keep digits and plus only; a plus is meaningful only in first position
  let cleaned = pasted.replace(/[^\d+]/g, '')
  cleaned = cleaned.replace(/(?!^)\+/g, '')

  // 0039... -> +39...
  if (cleaned.startsWith('00')) {
    cleaned = `+${cleaned.slice(2)}`
  }

  phoneNumber.value = cleaned
}
const emailRule = (val) =>
  !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val) || t('form.field.invalidEmail')
// const phoneRule = (val) => {
//   const e164Regex = /^\+[1-9]\d{1,14}$/
//   return e164Regex.test(val) || t('form.field.invalidPhoneNumber')
// }
const registerAsync = async() => {
  try {
    loading.value = true
    const res = await websiteStore.registerAsync(username.value, password.value, firstName.value, lastName.value, phoneNumber.value, email.value)
    notifyInfo(t(res.data?.message))
    router.push('/login')
  } catch (error) {
    const errors = error.response?.data || {}
    for (const field in errors) {
      if (errors.hasOwnProperty(field)) {
        errors[field].forEach((errorMsg) => {
          notifyError(t(errorMsg))
        })
      }
    }
  } finally {
    loading.value = false
  }
}

</script>

<template>
  <q-layout view="lHh Lpr lFf">
    <q-page-container>
      <q-page class="flex flex-center bg-gradient">
        <div class="column items-center q-gutter-md">
          <div class="text-h5">
            {{ $t('auth.signUp') }}
          </div>

          <q-form
            class="q-mt-lg column login-card"
            @submit.prevent="registerAsync"
          >
            <q-input
              v-model="username"
              outlined
              :placeholder="$t('auth.username')"
              :rules="[required]"
              autocapitalize="off"
            >
              <template #prepend>
                <q-icon name="mdi-account" />
              </template>
            </q-input>

            <q-input
              v-model="password"
              outlined
              class="q-mt-md"
              :type="isPwd ? 'password' : 'text'"
              :placeholder="$t('auth.password')"
              :rules="[required]"
            >
              <template #prepend>
                <q-icon name="mdi-lock" />
              </template>
              <template #append>
                <q-icon
                  :name="isPwd ? 'mdi-eye-off' : 'mdi-eye'"
                  class="cursor-pointer"
                  @click="isPwd = !isPwd"
                />
              </template>
            </q-input>

            <q-input
              v-model="firstName"
              outlined
              class="q-mt-md"
              :placeholder="$t('auth.firstName')"
              :rules="[required]"
            >
              <template #prepend>
                <q-icon name="mdi-account" />
              </template>
            </q-input>

            <q-input
              v-model="lastName"
              outlined
              class="q-mt-md"
              :placeholder="$t('auth.lastName')"
              :rules="[required]"
            >
              <template #prepend>
                <q-icon name="mdi-account" />
              </template>
            </q-input>

            <!-- <q-input
              v-model="phoneNumber"
              type="tel"
              outlined
              class="q-mt-md"
              :placeholder="$t('auth.phoneNumber')"
              :rules="[required,phoneRule]"
            >
              <template #prepend>
                <q-icon name="mdi-phone" />
              </template>
            </q-input> -->
            <q-tel-input
              v-model="phoneNumber"
              class="q-mt-md"
              :locales="locale"
              dark
              @paste.capture="onPhonePaste"
            />
            <q-input
              v-model="email"
              class="q-mt-md"
              :placeholder="$t('auth.email')"
              :rules="[required, emailRule]"
            >
              <template #prepend>
                <q-icon name="mdi-email" />
              </template>
            </q-input>

            <div class="q-mt-lg">
              <q-btn
                color="primary"
                class="full-width q-pa-sm"
                :label="$t('auth.register')"
                :loading="loading"
                type="submit"
              />
            </div>

            <div class="login-actions column items-center q-mt-md">
              <div class="no-account row items-center">
                <span class="text-white">{{ $t('auth.alreadyHaveAccount') }}</span>
                <router-link
                  to="/login"
                  class="signup-link q-ml-sm"
                >
                  {{ $t('auth.signIn') }}
                </router-link>
              </div>
            </div>
          </q-form>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/auth-forms.scss';
</style>
